import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import AppLeftSideBar from "../../components/SideBar/AppLeftSideBar";
import Button from "../../components/FormComponents/Button";
import Input from "../../components/FormComponents/Input";
import { getAppWorkflows, getAuditFilters, getSubjectConfigs, saveSubjectConfig } from "../../services/apis/auditApis";
import "../Monitor/monitor.css";
import "./audit.css";

const EMPTY_FIELDS = [
  { label: "", field_id: "" },
  { label: "", field_id: "" },
  { label: "", field_id: "" }
];

const hasAnyField = (fields) => (fields || []).some((f) => f?.field_id);

const AuditConfig = ({ embedded = false, orgId }) => {
  const [apps, setApps] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [addAppId, setAddAppId] = useState("");
  const [addWfId, setAddWfId] = useState("");
  const [addWorkflows, setAddWorkflows] = useState([]);
  const [editing, setEditing] = useState(null); // { wf_id, app_id, wf_name, fields, isNew }
  const [saving, setSaving] = useState(false);

  const appNameById = useMemo(() => {
    const map = {};
    apps.forEach((a) => (map[a.app_id] = a.app_name));
    return map;
  }, [apps]);

  const loadConfigs = useCallback(async () => {
    try {
      const response = await getSubjectConfigs();
      setConfigs((response?.data || []).filter((c) => hasAnyField(c.fields)));
    } catch (error) {
      console.error("Failed to fetch subject configs:", error);
      setConfigs([]);
    }
  }, []);

  useEffect(() => {
    getAuditFilters({ all: "true", org_id: orgId || undefined })
      .then((response) => setApps(response?.data?.apps || []))
      .catch(() => setApps([]));
    loadConfigs();
  }, [loadConfigs, orgId]);

  // workflows for the selected "add" app — sourced from the builder config, so
  // they show up before any export has run (no chicken-and-egg with export)
  useEffect(() => {
    if (!addAppId) {
      setAddWorkflows([]);
      setAddWfId("");
      return;
    }
    getAppWorkflows(addAppId)
      .then((response) => setAddWorkflows(response?.data?.workflows || []))
      .catch(() => setAddWorkflows([]));
    setAddWfId("");
  }, [addAppId]);

  // configs scoped to this org's apps. Configured-ness is per (app, workflow):
  // cloned apps share workflow ids, so wf_id alone would hide a clone's
  // workflow once any sibling app configures it.
  const orgConfigs = useMemo(() => configs.filter((c) => appNameById[c.app_id]), [configs, appNameById]);
  const configuredKeys = useMemo(() => new Set(orgConfigs.map((c) => `${c.app_id}|${c.wf_id}`)), [orgConfigs]);
  const unconfiguredWorkflows = useMemo(
    () => addWorkflows.filter((w) => !configuredKeys.has(`${addAppId}|${w.wf_id}`)),
    [addWorkflows, configuredKeys, addAppId]
  );

  const startAdd = () => {
    if (!addAppId || !addWfId) {
      toast.error("Choose an application and workflow to add");
      return;
    }
    const wf = addWorkflows.find((w) => w.wf_id === addWfId);
    setEditing({
      wf_id: addWfId,
      app_id: addAppId,
      wf_name: wf?.wf_name || "",
      fields: EMPTY_FIELDS.map((f) => ({ ...f })),
      wfFields: wf?.fields || [],
      isNew: true
    });
  };

  const startEdit = (cfg) => {
    setEditing({
      wf_id: cfg.wf_id,
      app_id: cfg.app_id,
      wf_name: cfg.wf_name,
      fields: [0, 1, 2].map((i) => ({
        label: cfg.fields?.[i]?.label || "",
        field_id: cfg.fields?.[i]?.field_id || ""
      })),
      wfFields: [],
      isNew: false
    });
    // load selectable fields for this workflow (config picker), best-effort
    getAppWorkflows(cfg.app_id)
      .then((response) => {
        const wf = (response?.data?.workflows || []).find((w) => w.wf_id === cfg.wf_id);
        if (wf?.fields?.length) {
          setEditing((prev) => (prev && prev.wf_id === cfg.wf_id ? { ...prev, wfFields: wf.fields } : prev));
        }
      })
      .catch(() => {});
  };

  const updateField = (idx, key, value) => {
    setEditing((prev) => ({
      ...prev,
      fields: prev.fields.map((f, i) => (i === idx ? { ...f, [key]: value } : f))
    }));
  };

  // pick a field from the workflow's field list: set its id, and auto-fill the
  // label when the name is still blank
  const pickField = (idx, fieldId) => {
    setEditing((prev) => {
      const wf = (prev.wfFields || []).find((f) => f.field_id === fieldId);
      return {
        ...prev,
        fields: prev.fields.map((f, i) =>
          i === idx ? { ...f, field_id: fieldId, label: f.label || wf?.label || "" } : f
        )
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSubjectConfig({
        wf_id: editing.wf_id,
        app_id: editing.app_id,
        wf_name: editing.wf_name,
        fields: editing.fields
      });
      toast.success("Saved. Existing rows are refreshing in the background.");
      setEditing(null);
      setAddAppId("");
      setAddWfId("");
      await loadConfigs();
    } catch (error) {
      console.error("Failed to save subject config:", error);
    } finally {
      setSaving(false);
    }
  };

  const renderSubjectCell = (field) => {
    if (!field?.field_id) return <span className="color_secondary">—</span>;
    return (
      <div className="d_flex flex_column">
        <span>{field.label || <span className="color_secondary">(no label)</span>}</span>
        <span className="audit_mono color_secondary font_size_xsmall">{field.field_id}</span>
      </div>
    );
  };

  const content = (
    <div
      className={`d_flex flex_column gap_2 w_100 overflow_auto ${embedded ? "flex_1" : "h_100 p_3"}`}
      style={{ minHeight: 0 }}
    >
      {!embedded && (
        <div className="w_100 d_flex justify_between align_center">
          <h2>Audit Settings — Subject Identifiers</h2>
        </div>
      )}

      <p className="font_size_small color_secondary" style={{ maxWidth: 780 }}>
        Configure up to 3 fields that identify the real-world subject of a workflow instance (e.g. a truck registration
        number, or a customer SAP id and application id). Field ids are set <b>per workflow</b>. During export these
        values are copied from the instance data onto every audit row, shown as columns, and made searchable. Each
        subject has a <b>name</b> (the column label) and a <b>field id</b> (the instance form field).
      </p>

      {/* Add a workflow */}
      <div className="d_flex gap_2 align_center flex_wrap">
        <select
          className="builder_input"
          style={{ minWidth: 240 }}
          value={addAppId}
          onChange={(e) => setAddAppId(e.target.value)}
        >
          <option value="">Application…</option>
          {apps.map((app) => (
            <option key={app.app_id} value={app.app_id}>
              {app.app_name}
            </option>
          ))}
        </select>
        <select
          className="builder_input"
          style={{ minWidth: 240 }}
          value={addWfId}
          onChange={(e) => setAddWfId(e.target.value)}
          disabled={!addAppId}
        >
          <option value="">Workflow…</option>
          {unconfiguredWorkflows.map((w) => (
            <option key={w.wf_id} value={w.wf_id}>
              {w.wf_name}
            </option>
          ))}
        </select>
        <Button variant="primary" onClick={startAdd} disabled={!addWfId}>
          Add
        </Button>
      </div>

      {/* Editor panel */}
      {editing && (
        <div className="audit_header_card" style={{ maxWidth: 820, display: "block" }}>
          <div className="d_flex justify_between align_center mb_2">
            <b>
              {editing.isNew ? "Add" : "Edit"} — {appNameById[editing.app_id] || editing.app_id} ·{" "}
              {editing.wf_name || editing.wf_id}
            </b>
          </div>
          {editing.fields.map((f, idx) => (
            <div key={idx} className="d_flex gap_2 mb_2" style={{ alignItems: "flex-end" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                <label className="builder_label">Subject {idx + 1} name</label>
                <Input
                  placeholder={idx === 0 ? "e.g. Truck Reg No" : "Name (optional)"}
                  value={f.label}
                  onChange={(e) => updateField(idx, "label", e.target.value)}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                <label className="builder_label">Field</label>
                {editing.wfFields?.length > 0 && (
                  <select
                    className="builder_input"
                    value={editing.wfFields.some((wf) => wf.field_id === f.field_id) ? f.field_id : ""}
                    onChange={(e) => pickField(idx, e.target.value)}
                  >
                    <option value="">Choose field…</option>
                    {editing.wfFields.map((wf) => (
                      <option key={wf.field_id} value={wf.field_id}>
                        {wf.label || wf.key || wf.field_id}
                        {wf.type ? ` · ${wf.type}` : ""}
                      </option>
                    ))}
                  </select>
                )}
                <Input
                  placeholder="instance form field id"
                  value={f.field_id}
                  onChange={(e) => updateField(idx, "field_id", e.target.value)}
                />
              </div>
            </div>
          ))}
          <div className="d_flex justify_end gap_2 mt_2">
            <Button variant="secondary" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} isLoading={saving} disabled={saving}>
              Save
            </Button>
          </div>
        </div>
      )}

      {/* Configured workflows table */}
      <div className="monitor_container">
        <div className="table-wrapper" style={{ height: "auto", maxHeight: "calc(100vh - 340px)", overflow: "auto" }}>
          <table className="log-table">
            <thead>
              <tr>
                <th>Application</th>
                <th>Workflow</th>
                <th>Subject 1</th>
                <th>Subject 2</th>
                <th>Subject 3</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {orgConfigs.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: "16px", color: "#6b7280" }}>
                    No workflows configured yet. Use the pickers above to add one.
                  </td>
                </tr>
              ) : (
                orgConfigs.map((cfg) => (
                  <tr key={`${cfg.app_id}|${cfg.wf_id}`}>
                    <td>{appNameById[cfg.app_id] || cfg.app_id}</td>
                    <td>{cfg.wf_name || cfg.wf_id}</td>
                    <td>{renderSubjectCell(cfg.fields?.[0])}</td>
                    <td>{renderSubjectCell(cfg.fields?.[1])}</td>
                    <td>{renderSubjectCell(cfg.fields?.[2])}</td>
                    <td>
                      <Button variant="secondary" size="small" onClick={() => startEdit(cfg)}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  if (embedded) return content;
  return (
    <div className="application_container">
      <AppLeftSideBar type="org" />
      {content}
    </div>
  );
};

export default AuditConfig;
